import React from 'react'
import styled from 'styled-components'
import { Box, Button, Grid, Stack, Text } from '@lojinha/design-system'
import { testId } from '@lojinha/helpers'
import { beholder } from '@lojinha/beholder'
import { DayCalendar } from '../day-calendar'
import {
  numberToCurrency,
  numberToCurrencyAriaLabel,
} from '../../helpers/formatNumber'
import { t } from '../../dictionary'
import { PurchasesVariants } from '../../core/purchase-variants'
import { Router } from '../../../router'
import { purchaseCardStyles } from './variants'

const Content = styled(Box)(
  ({ theme }) => `
  grid-template-columns: repeat(10, 1fr);
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.gray200};
  border-radius: ${theme.space.x8};

  ${theme.media.below('sm')} {
    flex-direction: column;
    justify-content: space-between;
  }
`
)

const DetailsButton = styled(Button)(
  ({ theme }) => `
    max-width: 130px;
    border-color: ${theme.colors.gray200};

    &:hover {
      border-color: ${theme.colors.success};
    }

    ${theme.media.above('md')}{
      width: 154px;
    }
  `
)

export type PurchaseCardProps = {
  orderId: number
  deliverySettings: {
    date: Date
    period: {
      name: string
      timeRange: number[]
    }
  }
  purchaseValue: number
  variant?: PurchasesVariants
}

export const PurchaseCard = ({
  orderId,
  deliverySettings,
  purchaseValue,
  variant = PurchasesVariants.Approved,
}: PurchaseCardProps) => {
  const { date } = deliverySettings
  const deliveryDay = date.getDate()

  const emitViewPurchaseDetailsEvent = () => {
    beholder.shot('structuredEvent', {
      category: 'my_purchases',
      action: 'view_purchase_details',
    })
  }

  const goToDetails = () => Router.push(`/meu/minhas-compras/${orderId}`)

  const onDetailsClick = () => {
    emitViewPurchaseDetailsEvent()
    goToDetails()
  }

  return (
    <Content
      display={'grid'}
      padding={['x16', 'x40']}
      height="130px"
      width="100%"
    >
      <Grid gridColumn={['1/11', '1/7']}>
        <Stack
          layout="horizontal"
          margin={['x0', 'x16']}
          alignItems={['flex-start', 'center']}
        >
          <Box display={['none', 'block']}>
            <DayCalendar
              dayNumber={deliveryDay}
              month={t.date.deliveryMonth(date)}
              variant={purchaseCardStyles[variant].dayCalendarVariant}
              tabindex={-1}
            />
          </Box>
          <Stack layout="vertical" margin="x0">
            <Text
              textSize={['x14', 'x2']}
              color={purchaseCardStyles[variant].color}
              {...testId('purchase-card-title')}
              aria-label={`, ${purchaseCardStyles[variant].title} ${orderId},`}
            >
              {purchaseCardStyles[variant].title} {orderId}
            </Text>
            <Text textSize={['x12', 'x14']} color="gray700">
              {t.deliveryDate} {t.date.fullyDeliveryDate(deliverySettings)}
            </Text>
          </Stack>
        </Stack>
      </Grid>
      <Grid gridColumn={['1/5', '7/9']}>
        <Box
          display="flex"
          alignItems={['flex-end', 'center']}
          justifyContent={['flex-start', 'center']}
        >
          <Text
            aria-label={`${t.purchaseValue}${numberToCurrencyAriaLabel(
              purchaseValue
            )}`}
          >
            {numberToCurrency(purchaseValue)}
          </Text>
        </Box>
      </Grid>
      <Grid gridColumn={['5/11', '9/11']}>
        <Box display="flex" alignItems="center" justifyContent="flex-end">
          <DetailsButton
            isOutlined
            color="success"
            size="small"
            isBlock
            onClick={onDetailsClick}
            {...testId('purchase-card-details')}
          >
            {t.seeDetails}
          </DetailsButton>
        </Box>
      </Grid>
    </Content>
  )
}
